import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { cn } from '~/lib/utils';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import type { SortDir } from '~/lib/grid/sort';

interface SortableHeaderProps {
	/** Untranslated-but-localised header label; Greek-capsed here (mirrors the grids' headers). */
	label: string;
	align?: 'left' | 'right' | 'center';
	/** This column's active sort direction, or null when it isn't the current sort column. */
	active: SortDir | null;
	/** Omit for a non-sortable column — renders the plain label with no affordance. */
	onSort?: () => void;
}

/**
 * Sortable column-header affordance shared by the Products and Orders grids. Renders the
 * Greek-capsed label and an asc/desc/idle chevron, matching the SystemGrid header (the icon
 * column stays neutral until the column is the active sort). Presentational only — the parent
 * owns the sort state and cycling.
 */
export function SortableHeader({ label, align = 'left', active, onSort }: SortableHeaderProps) {
	const text = toGreekCaps(label);
	if (!onSort) return <>{text}</>;
	return (
		<button
			type="button"
			onClick={onSort}
			className={cn(
				'inline-flex items-center gap-1 hover:text-foreground',
				align === 'right' && 'flex-row-reverse',
				align === 'center' && 'mx-auto',
				active && 'text-foreground'
			)}>
			{text}
			{active ? (
				active === 'asc' ? (
					<ChevronUp size={12} />
				) : (
					<ChevronDown size={12} />
				)
			) : (
				<ChevronsUpDown size={12} className="text-foreground-disabled" />
			)}
		</button>
	);
}
